import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Stack,
    CircularProgress,
    Dialog,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { FoodsIcon } from "@foodpilot/foods";
import { useCreateWebhook } from "src/api/client-api/applications/useCreateWebhook";
import { parseSerializationGroups } from "./parseSerializationGroups";
import { webhookEventOptions } from "./webhookEvents";

// Define the webhook type
interface Webhook {
    id: string;
    name?: string;
    endpoint?: string;
    eventName?: string;
    active: boolean;
    createdAt?: string;
    method?: string;
    headers?: Record<string, string>;
    retryCount?: number;
    application?: string;
    serializationGroups?: Array<string>;
}

const EMPTY_WEBHOOK_FORM = {
    name: "",
    endpoint: "",
    eventName: "",
    method: "POST",
    headersText: "{}",
    serializationGroupText: "",
    retryCount: 3,
};

const webhookToForm = (editingWebhook?: Webhook | null) => {
    if (!editingWebhook) {
        return EMPTY_WEBHOOK_FORM;
    }

    return {
        name: editingWebhook.name || "",
        endpoint: editingWebhook.endpoint || "",
        eventName: editingWebhook.eventName || "",
        method: editingWebhook.method || "POST",
        headersText: JSON.stringify(editingWebhook.headers || {}, null, 2),
        serializationGroupText: editingWebhook.serializationGroups ? editingWebhook.serializationGroups.join(",") : "",
        retryCount: editingWebhook.retryCount || 3,
    };
};

const WebhookModal = ({
    open,
    onClose,
    onSuccess,
    editingWebhook,
    applicationId,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editingWebhook?: Webhook | null;
    applicationId: string;
}) => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const initialForm = webhookToForm(editingWebhook);
    const [name, setName] = useState(() => initialForm.name);
    const [endpoint, setEndpoint] = useState(() => initialForm.endpoint);
    const [eventName, setEventName] = useState(() => initialForm.eventName);
    const [method, setMethod] = useState(() => initialForm.method);
    const [headersText, setHeadersText] = useState(() => initialForm.headersText);
    const [serializationGroupText, setSerializationGroupText] = useState(() => initialForm.serializationGroupText);
    const [retryCount, setRetryCount] = useState(() => initialForm.retryCount);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const createWebhookMutation = useCreateWebhook();
    const isCreating = createWebhookMutation.isLoading;

    const isEditing = !!editingWebhook;

    const availableEvents = webhookEventOptions(initialForm.eventName);

    const availableMethods = ["POST", "GET", "PUT", "DELETE"];

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!name.trim()) {
            errors.name = t("applications.nameRequired", "Name is required");
        } else if (name.length < 3) {
            errors.name = t("applications.nameTooShort", "Name must be at least 3 characters long");
        } else if (name.length > 255) {
            errors.name = t("applications.nameTooLong", "Name must be 255 characters or less");
        }

        if (!endpoint.trim()) {
            errors.endpoint = t("applications.endpointRequired", "Endpoint is required");
        } else if (!/^https?:\/\/.+/.test(endpoint)) {
            errors.endpoint = t("applications.invalidUrl", "Endpoint must be a valid HTTP or HTTPS URL");
        }

        if (!eventName) {
            errors.eventName = t(
                "applications.eventRequired",
                "Event name is required. Select from the Events dropdown.",
            );
        }

        try {
            if (headersText && headersText !== "{}") {
                JSON.parse(headersText);
            }
        } catch (err) {
            errors.headers = t("applications.invalidJson", "Headers must be valid JSON");
        }

        if (isNaN(retryCount) || retryCount < 0) {
            errors.retryCount = t("applications.invalidRetryCount", "Retry count must be a non-negative number");
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            let headers = {};
            try {
                headers = JSON.parse(headersText);
            } catch (err) {
                headers = {};
            }

            // Create webhook data with proper types - EXACTLY matching API requirements
            const webhookData = {
                name: name.trim(),
                endpoint: endpoint.trim(),
                eventName: eventName.trim(),
                method,
                headers,
                active: true,
                retryCount: Math.min(Math.max(0, retryCount), 5),
                serializationGroups: parseSerializationGroups(serializationGroupText),
            };

            await createWebhookMutation.mutateAsync({
                applicationId,
                webhook: webhookData,
            });

            setSnackbarProps({
                message:
                    isEditing ?
                        t("applications.webhooks.updateSuccess", "Webhook updated successfully")
                    :   t("applications.webhooks.createSuccess", "Webhook created successfully"),
                type: "success",
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            if (error && typeof error === "object") {
                setFormErrors(error);
            } else {
                setSnackbarProps({
                    message:
                        isEditing ?
                            t("applications.webhooks.updateError", "Failed to update webhook")
                        :   t("applications.webhooks.createError", "Failed to create webhook"),
                    type: "error",
                });
            }
        }
    };

    const isFormValid = name.trim().length > 0 && endpoint.trim().length > 0 && eventName.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    maxHeight: "90vh",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {isEditing ?
                            t("applications.editWebhook", "Edit Webhook")
                        :   t("applications.addWebhook", "Add New Webhook")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {isEditing ?
                            t("applications.editWebhookDescription", "Update the parameters for your webhook.")
                        :   t("applications.createWebhookDescription", "Set the parameters for your webhook.")}
                    </Typography>
                </Box>
                <Box
                    onClick={onClose}
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "50%",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            {/* Content */}
            <Box
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                    maxHeight: "calc(90vh - 200px)",
                    overflowY: "auto",
                }}
            >
                <Stack spacing={3}>
                    {/* Name Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("applications.webhookName", "Webhook Name")}:
                        </Typography>
                        <TextField
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("applications.webhookNamePlaceholder", "Enter webhook name")}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "0px 16px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Event Name Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("applications.eventName", "Event Name")}:
                        </Typography>
                        <FormControl fullWidth error={!!formErrors.eventName}>
                            <Select
                                value={eventName}
                                onChange={(e: SelectChangeEvent) => setEventName(e.target.value)}
                                sx={{
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                    "& .MuiSelect-select": {
                                        fontSize: "0.875rem",
                                        padding: "0px 16px",
                                        color: "#393A36",
                                    },
                                }}
                            >
                                {availableEvents.map((event) => (
                                    <MenuItem key={event} value={event}>
                                        {event}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.eventName && (
                                <Typography
                                    variant="small"
                                    sx={{
                                        color: "error.main",

                                        marginTop: "4px",
                                        marginLeft: "14px",
                                    }}
                                >
                                    {formErrors.eventName}
                                </Typography>
                            )}
                        </FormControl>
                    </Box>

                    {/* Endpoint Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("applications.endpoint", "Endpoint")}:
                        </Typography>
                        <TextField
                            fullWidth
                            value={endpoint}
                            onChange={(e) => setEndpoint(e.target.value)}
                            placeholder="https://example.com/api/webhook"
                            error={!!formErrors.endpoint}
                            helperText={formErrors.endpoint}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "0px 16px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Method and Retry Count Row */}
                    <Stack direction="row" spacing={2}>
                        <Box flex={1}>
                            <Typography
                                variant="h6"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                            >
                                {t("applications.httpMethod", "HTTP Method")}:
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={method}
                                    onChange={(e: SelectChangeEvent) => setMethod(e.target.value)}
                                    sx={{
                                        borderRadius: "24px",
                                        height: "40px",
                                        backgroundColor: "white",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#E7E6E4",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#D0D0D0",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: (theme) => theme.palette.primary[600],
                                        },
                                        "& .MuiSelect-select": {
                                            fontSize: "0.875rem",
                                            padding: "0px 16px",
                                            color: "#393A36",
                                        },
                                    }}
                                >
                                    {availableMethods.map((methodOption) => (
                                        <MenuItem key={methodOption} value={methodOption}>
                                            {methodOption}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box flex={1}>
                            <Typography
                                variant="h6"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                            >
                                {t("applications.retryCount", "Retry Count")}:
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                value={retryCount}
                                onChange={(e) => setRetryCount(Number(e.target.value))}
                                error={!!formErrors.retryCount}
                                helperText={formErrors.retryCount}
                                InputProps={{ inputProps: { min: 0, max: 5 } }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "24px",
                                        height: "40px",
                                        backgroundColor: "white",
                                        "& fieldset": {
                                            borderColor: "#E7E6E4",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#D0D0D0",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: (theme) => theme.palette.primary[600],
                                        },
                                    },
                                    "& .MuiOutlinedInput-input": {
                                        fontSize: "0.875rem",
                                        padding: "0px 16px",
                                        color: "#393A36",
                                    },
                                }}
                            />
                        </Box>
                    </Stack>

                    {/* Headers Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("applications.headersJson", "Headers (JSON)")}:
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={headersText}
                            onChange={(e) => setHeadersText(e.target.value)}
                            placeholder='{"Content-Type": "application/json"}'
                            error={!!formErrors.headers}
                            helperText={
                                formErrors.headers ||
                                t(
                                    "applications.headersHelp",
                                    'Enter headers as a JSON object, e.g. {"Content-Type": "application/json"}',
                                )
                            }
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "12px 16px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("applications.serializationGroups", "Serialization groups")}:
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={serializationGroupText}
                            onChange={(e) => setSerializationGroupText(e.target.value)}
                            placeholder="e.g. ssq-post:read,ssq-post:scores"
                            error={!!formErrors.serializationGroups}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "12px 16px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={handleSave}
                    disabled={!isFormValid || isCreating}
                    sx={(theme) => ({
                        width: "150px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        opacity: isFormValid ? 1 : 0.36,
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                        },
                    })}
                >
                    {isCreating ?
                        <CircularProgress size={20} color="inherit" />
                    : isEditing ?
                        t("applications.update", "Update")
                    :   t("applications.save", "Save")}
                </Button>
            </Box>
        </Dialog>
    );
};

export { WebhookModal, type Webhook };
